// Simple email validation without external dependencies
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const normalizeEmail = (email) => {
  return email.toLowerCase().trim();
};

const isISO8601 = (dateStr) => {
  const iso8601Regex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;
  return iso8601Regex.test(dateStr) && !isNaN(Date.parse(dateStr));
};

// Rate limit storage — must be outside the class so it persists across calls
const _rateLimits = new Map();

class InputValidator {
  // Enhanced form data validation with comprehensive security
  static validateFormData(formData) {
    const errors = [];
    const sanitizedData = {};
    const warnings = [];

    // Enhanced email validation
    if (!formData.email) {
      errors.push('Email is required');
    } else if (!isValidEmail(formData.email)) {
      errors.push('Invalid email format');
    } else if (!formData.email.toLowerCase().endsWith('@amazon.com')) {
      errors.push('Email must be an Amazon email address (@amazon.com)');
    } else if (formData.email.length > 100) {
      errors.push('Email address too long (max 100 characters)');
    } else {
      sanitizedData.email = normalizeEmail(formData.email);
    }

    // Enhanced site code validation
    if (!formData.siteCode) {
      errors.push('Studio location is required');
    } else if (!/^BLR\d{2}$/.test(formData.siteCode)) {
      errors.push('Invalid studio code format (must be BLR followed by 2 digits)');
    } else if (!['BLR13', 'BLR14', 'BLR18', 'BLR26', 'BLR27'].includes(formData.siteCode)) {
      errors.push('Studio code not in allowed list');
    } else {
      sanitizedData.siteCode = formData.siteCode;
    }

    // Enhanced slot ID validation
    if (!formData.slotId) {
      errors.push('Time slot is required');
    } else if (!/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(formData.slotId)) {
      errors.push('Invalid time slot format (must be HH:MM-HH:MM)');
    } else {
      // Validate time slot is within business hours (9 AM - 5 PM)
      const [startTime, endTime] = formData.slotId.split('-');
      const [startHour] = startTime.split(':').map(Number);
      const [endHour] = endTime.split(':').map(Number);
      
      if (startHour < 9 || endHour > 17 || startHour >= endHour) {
        errors.push('Time slot must be within business hours (9 AM - 5 PM)');
      } else {
        sanitizedData.slotId = formData.slotId;
      }
    }

    // Queue position validation
    if (formData.queuePosition !== undefined) {
      const position = parseInt(formData.queuePosition);
      if (isNaN(position) || position < 1 || position > 20) {
        errors.push('Invalid queue position (must be 1-20)');
      } else {
        sanitizedData.queuePosition = position;
      }
    }

    // Enhanced sanitization of other fields
    sanitizedData.timeSlot = this.sanitizeString(formData.timeSlot, 20);
    sanitizedData.estimatedTime = this.sanitizeString(formData.estimatedTime, 50);
    sanitizedData.submissionDate = this.sanitizeString(formData.submissionDate, 50);
    sanitizedData.tokenNumber = this.sanitizeString(formData.tokenNumber, 30);
    
    // Validate token number format if provided
    if (formData.tokenNumber && !/^[A-Z0-9-]+$/.test(formData.tokenNumber)) {
      warnings.push('Token number contains invalid characters');
    }

    // Additional security checks
    this.performSecurityChecks(formData, errors, warnings);

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      sanitizedData
    };
  }

  // Security checks for potential attacks
  static performSecurityChecks(formData, errors, warnings) {
    const suspiciousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /\beval\s*\(/gi,
      /\balert\s*\(/gi,
      /\bdocument\./gi,
      /\bwindow\./gi
    ];

    Object.values(formData).forEach(value => {
      if (typeof value === 'string') {
        suspiciousPatterns.forEach(pattern => {
          if (pattern.test(value)) {
            errors.push('Input contains potentially malicious content');
          }
        });
      }
    });
  }

  // Enhanced update data validation
  static validateUpdateData(data) {
    const errors = [];
    const sanitizedData = {};
    const warnings = [];

    // Enhanced ID validation
    if (!data.id) {
      errors.push('ID is required');
    } else if (typeof data.id !== 'string') {
      errors.push('ID must be a string');
    } else if (data.id.length > 50) {
      errors.push('ID too long (max 50 characters)');
    } else if (!/^[\w-]+$/.test(data.id)) {
      errors.push('ID contains invalid characters (only alphanumeric, underscore, and dash allowed)');
    } else {
      sanitizedData.id = this.sanitizeString(data.id, 50);
    }

    // Enhanced status validation
    const validStatuses = ['Pending', 'In Progress', 'Completed', 'Cancelled', 'Expired'];
    if (data.status) {
      if (!validStatuses.includes(data.status)) {
        errors.push(`Invalid status. Must be one of: ${validStatuses.join(', ')}`);
      } else {
        sanitizedData.status = data.status;
      }
    }

    // Validate admin email if provided
    if (data.adminEmail) {
      const emailValidation = this.validateEmail(data.adminEmail);
      if (!emailValidation.valid) {
        errors.push(`Invalid admin email: ${emailValidation.error}`);
      } else {
        sanitizedData.adminEmail = normalizeEmail(data.adminEmail);
      }
    }

    // Validate timestamp if provided
    if (data.timestamp && !isISO8601(data.timestamp)) {
      warnings.push('Invalid timestamp format');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
      sanitizedData
    };
  }

  // Enhanced email validation
  static validateEmail(email) {
    if (!email) {
      return { valid: false, error: 'Email is required' };
    }
    if (typeof email !== 'string') {
      return { valid: false, error: 'Email must be a string' };
    }
    if (email.length > 100) {
      return { valid: false, error: 'Email too long (max 100 characters)' };
    }
    if (!isValidEmail(email)) {
      return { valid: false, error: 'Invalid email format' };
    }
    if (!email.toLowerCase().endsWith('@amazon.com')) {
      return { valid: false, error: 'Must be an Amazon email address' };
    }
    return { valid: true, sanitized: normalizeEmail(email) };
  }

  // Enhanced appointment ID validation
  static validateAppointmentId(id) {
    if (!id) {
      return { valid: false, error: 'Appointment ID is required' };
    }
    if (typeof id !== 'string') {
      return { valid: false, error: 'Appointment ID must be a string' };
    }
    if (id.length > 50) {
      return { valid: false, error: 'Appointment ID too long (max 50 characters)' };
    }
    if (!/^[\w-]+$/.test(id)) {
      return { valid: false, error: 'Appointment ID contains invalid characters' };
    }
    return { valid: true, sanitized: this.sanitizeString(id, 50) };
  }

  // Enhanced string sanitization with XSS protection
  static sanitizeString(str, maxLength = 100) {
    if (!str) return '';
    
    // Convert to string and trim
    let cleaned = str.toString().trim();
    
    // Remove null bytes and control characters
    cleaned = cleaned.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
    
    // Basic XSS protection — encode angle brackets and quotes only
    // Do NOT encode slashes — they appear in dates like "3/24/2026"
    cleaned = cleaned
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
    
    return cleaned.substring(0, maxLength);
  }

  // Validate date format and range
  static validateDate(dateStr) {
    if (!dateStr) {
      return { valid: false, error: 'Date is required' };
    }
    
    if (!isISO8601(dateStr)) {
      return { valid: false, error: 'Invalid date format (use YYYY-MM-DD)' };
    }
    
    const date = new Date(dateStr);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    
    if (date < todayStart) {
      return { valid: false, error: 'Date cannot be in the past' };
    }
    
    if (date > maxDate) {
      return { valid: false, error: 'Date cannot be more than 30 days in the future' };
    }
    
    // Check if it's a weekend
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return { valid: false, error: 'Studio is closed on weekends' };
    }
    
    return { valid: true, sanitized: dateStr };
  }

  // Rate limiting validation
  static validateRateLimit(clientIP, action) {
    const key = `${clientIP}:${action}`;
    const now = Date.now();
    const windowMs = 15 * 60 * 1000; // 15 minutes
    const maxRequests = action === 'booking' ? 5 : 20;
    
    if (!_rateLimits.has(key)) {
      _rateLimits.set(key, { count: 1, resetTime: now + windowMs });
      return { allowed: true };
    }
    
    const limit = _rateLimits.get(key);
    if (now > limit.resetTime) {
      _rateLimits.set(key, { count: 1, resetTime: now + windowMs });
      return { allowed: true };
    }
    
    if (limit.count >= maxRequests) {
      return { 
        allowed: false, 
        error: `Rate limit exceeded. Try again in ${Math.ceil((limit.resetTime - now) / 1000)} seconds.` 
      };
    }
    
    limit.count++;
    return { allowed: true };
  }
}

export default InputValidator;